import { zodResolver } from '@hookform/resolvers/zod'
import { Lock, Visibility, VisibilityOff } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, IconButton, TextField, Typography } from '@mui/material'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import { z } from 'zod'
import { formatCentsInEuros } from '../../domain/GuestAdoption/guestAdoptionDonation'
import { AuthClient } from '../../interactors/clients/AuthClient'
import { GuestAdoptionCheckoutDto } from '../../interactors/clients/GuestAdoptionsClient'
import { useGlobalSnackbarStore } from '../../store/GlobalSnackBarStore'
import { AdoptionEndOffers } from '../Adoptions/AdoptionEndOffers'
import { PATHS } from '../PATHS'
import { blue, colors } from '../theme'
import { GuestAdoptionAmountRow, GuestAdoptionCard, GuestAdoptionTitle } from './GuestAdoptionLayout'
import { GuestAdoptionRecap } from './GuestAdoptionRecap'

const passwordSchema = z
  .object({
    password: z.string().trim().min(8, 'Le mot de passe doit contenir au moins 8 caractères.'),
    passwordConfirm: z.string().trim(),
  })
  .refine((data) => data.password === data.passwordConfirm, {
    message: 'Les mots de passe ne correspondent pas.',
    path: ['passwordConfirm'],
  })

type PasswordFormData = z.infer<typeof passwordSchema>

interface Props {
  checkout: GuestAdoptionCheckoutDto
  justPaid: boolean
  sessionOpened: boolean
  openSession: () => Promise<boolean>
}

export const GuestAdoptionWelcome: React.FC<Props> = ({ checkout, justPaid, sessionOpened, openSession }) => {
  const navigate = useNavigate()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const [hasPassword, setHasPassword] = useState<boolean>(checkout.adopter.hasPassword)
  const [isPasswordField, setIsPasswordField] = useState(true)
  const [isOpeningSpace, setIsOpeningSpace] = useState<boolean>(false)

  const { adopter, animal } = checkout
  const donationInCents = checkout.donationInCents ?? 0
  // An adopter who already owns a password and comes back later signs in as usual.
  const mustSignIn = hasPassword && !sessionOpened

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    mode: 'onSubmit',
  })

  const onPasswordSubmit: SubmitHandler<PasswordFormData> = async ({ password }) => {
    try {
      if (!sessionOpened && !(await openSession())) {
        throw new Error('The session could not be opened')
      }

      // Sent as typed, like the existing password creation page: the backend hashes what it receives.
      await AuthClient.updatePassword(password)
      setHasPassword(true)
      globalSnackbarStore.triggerSuccessMessage(`Votre accès a bien été créé !`)
    } catch {
      globalSnackbarStore.triggerErrorMessage(
        'Une erreur est survenue lors de la création de votre mot de passe. Vous pouvez aussi utiliser le lien reçu par email.'
      )
    }
  }

  const handleOpenSpace = async () => {
    if (mustSignIn) {
      return navigate(`${PATHS.login.absolute}?email=${encodeURIComponent(adopter.email)}`)
    }

    setIsOpeningSpace(true)
    const isSessionOpened = sessionOpened || (await openSession())
    setIsOpeningSpace(false)

    if (!isSessionOpened) {
      return globalSnackbarStore.triggerErrorMessage(
        `Nous n'avons pas pu ouvrir votre espace. Utilisez le lien reçu par email pour y accéder.`
      )
    }

    navigate('/')
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
        <Typography variant="h1" sx={{ color: blue, fontFamily: 'Insaniburger', fontSize: { xs: '34px', sm: '44px' } }}>
          {justPaid ? 'Bienvenue sur Petso !' : 'Adoption finalisée'}
        </Typography>
        <Typography variant="body1" sx={{ color: blue, fontWeight: 600 }}>
          {`Merci ${adopter.firstName}, votre paiement est confirmé : l'adoption de ${animal.name} est finalisée 🎉`}
        </Typography>
        <Typography variant="body2" sx={{ color: '#696B6D' }}>
          {`Un reçu vous est envoyé à l'adresse ${adopter.email}.`}
        </Typography>
      </Box>

      <GuestAdoptionRecap checkout={checkout}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, borderTop: '1px solid #CCCCCC', pt: 2 }}>
          <GuestAdoptionAmountRow label="Frais d'adoption :" amount={formatCentsInEuros(checkout.amountInCents)} />
          {donationInCents > 0 && (
            <GuestAdoptionAmountRow label="Votre contribution à Petso :" amount={formatCentsInEuros(donationInCents)} />
          )}
          <GuestAdoptionAmountRow
            label={checkout.paidAt ? `Total payé le ${dayjs(checkout.paidAt).format('DD/MM/YYYY')} :` : 'Total payé :'}
            amount={formatCentsInEuros(checkout.amountInCents + donationInCents)}
          />
        </Box>
      </GuestAdoptionRecap>

      <GuestAdoptionCard>
        {hasPassword ? (
          <>
            <GuestAdoptionTitle>Votre espace Petso</GuestAdoptionTitle>
            <Typography variant="body2" sx={{ color: '#696B6D' }}>
              {`Retrouvez-y les informations de ${animal.name}, ses rappels de santé, des conseils et vos cadeaux de bienvenue.`}
            </Typography>
          </>
        ) : (
          <>
            <GuestAdoptionTitle>Créez votre espace Petso</GuestAdoptionTitle>
            <Typography variant="body2" sx={{ color: '#696B6D' }}>
              {`Facultatif : choisissez un mot de passe pour retrouver à tout moment les informations de ${animal.name}, ses rappels de santé, des conseils et vos cadeaux de bienvenue.`}
            </Typography>
            <Typography variant="body2" sx={{ color: '#696B6D', fontStyle: 'italic' }}>
              {`Utilisez votre mail ${adopter.email} pour retrouver la fiche animal de ${animal.name}.`}
            </Typography>
            <form onSubmit={handleSubmit(onPasswordSubmit)}>
              <TextField
                fullWidth
                type={isPasswordField ? 'password' : 'text'}
                label="Mot de passe"
                autoComplete="new-password"
                {...register('password')}
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={{
                  startAdornment: <Lock sx={{ marginRight: '8px' }} />,
                  endAdornment: (
                    <IconButton
                      color="default"
                      size="small"
                      edge="end"
                      onClick={() => setIsPasswordField(!isPasswordField)}
                    >
                      {isPasswordField ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  ),
                }}
              />
              <TextField
                fullWidth
                type={isPasswordField ? 'password' : 'text'}
                label="Confirmation du mot de passe"
                autoComplete="new-password"
                {...register('passwordConfirm')}
                error={!!errors.passwordConfirm}
                helperText={errors.passwordConfirm?.message}
                sx={{ marginTop: '1.5rem' }}
              />
              <LoadingButton
                variant="contained"
                type="submit"
                fullWidth
                loading={isSubmitting}
                sx={{ fontSize: '18px', mt: 2 }}
              >
                Me créer un compte
              </LoadingButton>
            </form>
          </>
        )}
        <LoadingButton
          variant={hasPassword ? 'contained' : 'outlined'}
          color="primary"
          fullWidth
          loading={isOpeningSpace}
          disabled={isSubmitting}
          sx={{ fontSize: '18px' }}
          onClick={handleOpenSpace}
        >
          {mustSignIn ? 'Me connecter' : hasPassword ? 'Accéder à mon espace' : 'Plus tard, accéder à mon espace'}
        </LoadingButton>
      </GuestAdoptionCard>

      {sessionOpened && animal.species && (
        <Box sx={{ backgroundColor: 'white', borderRadius: 2, px: 2, overflow: 'hidden' }}>
          <Typography sx={{ color: colors.blueSky, fontSize: '20px', fontWeight: 'bold', pt: 2, pb: 1 }}>
            {`Nos offres partenaires pour ${animal.name}`}
          </Typography>
          <AdoptionEndOffers animalName={animal.name} animalId={animal.id} species={animal.species} />
        </Box>
      )}
    </>
  )
}
